import React, { Fragment, useMemo, useState } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { QuestTopicList } from '@models/Questionnaire/QuestTopicListModel';
import { QuestTopicItem } from './QuestTopicItem';
import {
  NoMatchingResultSvg,
  Separator,
  TextInput,
  BaseText,
  Loader,
} from '@components';

const TopicsContainer = styled(View)({
  flexGrow: 1,
  minHeight: [null, 338],
});

const TopicListContainer = styled(View)({
  flexDirection: 'row',
  flexWrap: 'wrap',
});

const NoResultContainer = styled(View)({
  alignItems: 'center',
  flex: 1,
});

const LoaderContainer = styled(View)({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: [null, 338],
});

const NoResultTitle = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
});

interface IQuestTopicsProps {
  list: QuestTopicList;
}

export const QuestTopics = observer<IQuestTopicsProps>(({ list }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  const data = useMemo(
    () => (input ? list.searchByText(input) : list.array),
    [input, list.array.length],
  );

  const isNothingFound = !!input && data.length === 0;

  return (
    <Fragment>
      <TextInput
        value={input}
        onChangeText={setInput}
        iconRight="search-light"
        placeholder={t('questionnaire.searchPlaceholder')}
        hasClearButton
      />
      <Separator size="$7" />
      {list.fetch.inProgress ? (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      ) : (
        <TopicsContainer>
          <TopicListContainer>
            {data.map((i) => (
              <QuestTopicItem key={i.id} item={i} list={list} />
            ))}
            {isNothingFound && (
              <NoResultContainer>
                <NoMatchingResultSvg />
                <Separator size="$4" />
                <NoResultTitle>
                  {`${t('questionnaire.noMatchingResult')} 🤷‍♂️`}
                </NoResultTitle>
                <Separator size="$4" />
              </NoResultContainer>
            )}
          </TopicListContainer>
        </TopicsContainer>
      )}
    </Fragment>
  );
});
